import { useMemo } from 'react';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import type { Aggregate } from 'src/components/followUp/common/types';

type UseExportOptionsProps = {
  data: Aggregate | null;
  comparisonData: Aggregate | null;
  labels: { name: string }[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const mapRows = (data: Aggregate | null, labels: { name: string }[]): Row[] => {
  if (!data) return [];

  return [
    [
      {
        value: labels[0].name,
        fontWeight: 'bold',
      },
      {
        value: data.value,
      },
      {
        value: `${getRelativeValue(data.value, data.totalObjects, 0)}%`,
        align: 'right',
      },
    ],
    [
      {
        value: labels[1].name,
        fontWeight: 'bold',
      },
      {
        value: data.totalObjects - data.value,
      },
      {
        value: `${getRelativeValue(
          data.totalObjects - data.value,
          data.totalObjects,
          0,
        )}%`,
        align: 'right',
      },
    ],
  ];
};

export const useExportOptions = ({
  data,
  comparisonData,
  labels,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions =>
  useMemo(() => {
    const rows = mapRows(data, labels);
    const comparisonRows = comparisonData
      ? mapRows(comparisonData, labels)
      : undefined;

    return {
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [data, labels, widgetName, queryFilters, comparisonData]);
